import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Building2, Users, Boxes, Search, Megaphone, FileText, UserCheck, Trash2, LogIn, Plus, X,
} from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend,
} from "recharts";
import api, { useTitle, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const TABS = [
  { key: "dashboard", label: "Dashboard" },
  { key: "firmalar", label: "Firmalar" },
  { key: "kullanicilar", label: "Kullanıcılar" },
  { key: "kategoriler", label: "Kategoriler" },
  { key: "konumlar", label: "İller / İlçeler" },
  { key: "kaynaklar", label: "Kaynaklar" },
  { key: "musaitlikler", label: "Müsaitlikler" },
  { key: "reklamlar", label: "Reklamlar" },
  { key: "talepler", label: "İletişim Talepleri" },
  { key: "abonelikler", label: "Abonelikler" },
  { key: "loglar", label: "Loglar" },
];

const PLACEMENTS = [
  ["home_top", "Ana sayfa üst banner"], ["hero_marquee", "Hero kayan bant"], ["home_category", "Ana sayfa kategori altı"],
  ["search_top", "Arama sonuçları üstü"], ["search_mid", "Arama sonuçları ortası"],
  ["search_bottom", "Arama sonuçları altı"], ["company_top", "Firma profil üstü"],
  ["company_bottom", "Firma profil altı"], ["mobile_banner", "Mobil banner"],
];
const COLORS = ["#D4AF37", "#10B981", "#2563EB", "#EF4444", "#F59E0B", "#8B5CF6", "#EC4899", "#14B8A6"];
const EMPTY_AD = { name: "", image: "", url: "", start_date: "2026-01-01", end_date: "2028-12-31", active: true, priority: 0, city: "", district: "", category_id: "", placement: "home_top" };

function Card({ label, value, icon: Icon, testid }) {
  return (
    <div className="card p-5" data-testid={testid}>
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-[#D4AF37]/10 border border-[#D4AF37]/25 flex items-center justify-center text-[#D4AF37]"><Icon size={18} /></div>
        <div><div className="text-2xl font-extrabold text-white">{value}</div><div className="text-xs text-slate-400">{label}</div></div>
      </div>
    </div>
  );
}

export default function AdminPanel() {
  const { user, impersonate } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "dashboard";
  const [stats, setStats] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [locations, setLocations] = useState([]);
  const [resources, setResources] = useState([]);
  const [availability, setAvailability] = useState([]);
  const [ads, setAds] = useState([]);
  const [requests, setRequests] = useState([]);
  const [plans, setPlans] = useState(null);
  const [logs, setLogs] = useState([]);
  const [selCity, setSelCity] = useState("Denizli");
  const [companyCat, setCompanyCat] = useState("");
  const [newCity, setNewCity] = useState("");
  const [newDistrict, setNewDistrict] = useState("");
  const [newCat, setNewCat] = useState({ name: "", parent_id: "", order: 99 });
  const [adModal, setAdModal] = useState(null);
  const [expandedCompany, setExpandedCompany] = useState(null);
  const [companyResources, setCompanyResources] = useState([]);
  useTitle("Admin Paneli");

  const isAdmin = user && (user.role === "ADMIN" || user.role === "SUPER_ADMIN");
  useEffect(() => {
    if (user === null) navigate("/giris");
    else if (user && !isAdmin) navigate("/");
    // eslint-disable-next-line
  }, [user]);

  useEffect(() => {
    if (!isAdmin) return;
    const load = {
      dashboard: () => api.get("/admin/stats").then((r) => setStats(r.data)),
      firmalar: () => api.get(`/admin/companies${companyCat ? `?category=${companyCat}` : ""}`).then((r) => setCompanies(r.data)),
      kullanicilar: () => api.get("/admin/users").then((r) => setUsers(r.data)),
      kategoriler: () => api.get("/admin/categories").then((r) => setCategories(r.data)),
      konumlar: () => api.get("/admin/locations").then((r) => setLocations(r.data)),
      kaynaklar: () => api.get("/admin/resources").then((r) => setResources(r.data)),
      musaitlikler: () => api.get("/admin/availability").then((r) => setAvailability(r.data)),
      reklamlar: () => api.get("/admin/ads").then((r) => setAds(r.data)),
      talepler: () => api.get("/admin/contact-requests").then((r) => setRequests(r.data)),
      abonelikler: () => api.get("/admin/plans").then((r) => setPlans(r.data)),
      loglar: () => api.get("/admin/audit-logs").then((r) => setLogs(r.data)),
    }[tab];
    load?.().catch((e) => toast.error(errMsg(e)));
    if (tab === "reklamlar" || tab === "kategoriler" || tab === "firmalar") api.get("/admin/categories").then((r) => setCategories(r.data)).catch(() => {});
    if (tab === "reklamlar") api.get("/admin/locations").then((r) => setLocations(r.data)).catch(() => {});
  }, [isAdmin, tab, companyCat]);

  if (!isAdmin) return null;

  const run = (p, msg) => p.then(() => msg && toast.success(msg)).catch((e) => toast.error(errMsg(e)));

  const updateCompany = (id, updates) =>
    run(api.put(`/admin/companies/${id}`, updates), "Firma güncellendi").then(() =>
      api.get(`/admin/companies${companyCat ? `?category=${companyCat}` : ""}`).then((r) => setCompanies(r.data)));

  const doImpersonate = async (c) => {
    try {
      const r = await api.post(`/admin/companies/${c.id}/impersonate`);
      impersonate(r.data.token, r.data.user, r.data.company);
      toast.success(`${c.name} olarak giriş yapıldı`);
      navigate("/firma-panel");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const toggleCompanyResources = async (c) => {
    if (expandedCompany === c.id) return setExpandedCompany(null);
    setExpandedCompany(c.id);
    const r = await api.get(`/admin/companies/${c.id}/resources`).catch(() => ({ data: [] }));
    setCompanyResources(r.data);
  };

  const saveAd = async (e) => {
    e.preventDefault();
    try {
      if (adModal.id) await api.put(`/admin/ads/${adModal.id}`, adModal.form);
      else await api.post("/admin/ads", adModal.form);
      toast.success("Reklam kaydedildi");
      setAdModal(null);
      api.get("/admin/ads").then((r) => setAds(r.data));
    } catch (err) { toast.error(errMsg(err)); }
  };

  const selCityObj = locations.find((l) => l.city === selCity);
  const parentCats = categories.filter((c) => !c.parent_id);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10" data-testid="admin-panel">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-extrabold text-white">Yönetim Paneli</h1>
          <p className="text-sm text-slate-400">{user.email} · <span className="text-[#D4AF37] font-semibold">{user.role}</span></p>
        </div>
      </div>

      <div className="flex gap-2 overflow-x-auto pb-2 mb-6">
        {TABS.map((t) => (
          <button key={t.key} data-testid={`admin-tab-${t.key}`} onClick={() => setParams({ tab: t.key })}
            className={`tab-btn ${tab === t.key ? "tab-btn-active" : ""}`}>{t.label}</button>
        ))}
      </div>

      {tab === "dashboard" && stats && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
            <Card icon={Building2} label="Toplam Firma" value={stats.cards.total_companies} testid="stat-total-companies" />
            <Card icon={UserCheck} label="Aktif Firma" value={stats.cards.active_companies} testid="stat-active-companies" />
            <Card icon={Users} label="Kullanıcı" value={stats.cards.total_users} testid="stat-total-users" />
            <Card icon={Boxes} label="Kaynak" value={stats.cards.total_resources} testid="stat-total-resources" />
            <Card icon={Search} label="Bugünkü Arama" value={stats.cards.searches_today} testid="stat-searches-today" />
            <Card icon={Megaphone} label="Aktif Reklam" value={stats.cards.active_ads} testid="stat-active-ads" />
            <Card icon={FileText} label="Talepler" value={stats.cards.contact_requests} testid="stat-requests" />
          </div>
          <div className="grid lg:grid-cols-3 gap-5">
            <div className="card p-5 lg:col-span-2">
              <h3 className="font-bold text-white mb-4">Arama Sayıları</h3>
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={stats.search_chart}>
                  <XAxis dataKey="date" stroke="#475569" fontSize={11} />
                  <YAxis stroke="#475569" fontSize={11} allowDecimals={false} />
                  <Tooltip contentStyle={{ background: "#0F172A", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12 }} />
                  <Line type="monotone" dataKey="count" stroke="#D4AF37" strokeWidth={2.5} dot={{ fill: "#D4AF37" }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="card p-5">
              <h3 className="font-bold text-white mb-4">Kategori Dağılımı</h3>
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={stats.category_chart} dataKey="value" nameKey="name" innerRadius={45} outerRadius={75}>
                    {stats.category_chart.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ background: "#0F172A", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="card p-5 lg:col-span-2">
              <h3 className="font-bold text-white mb-4">Firma Kayıtları</h3>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={stats.signup_chart}>
                  <XAxis dataKey="date" stroke="#475569" fontSize={11} />
                  <YAxis stroke="#475569" fontSize={11} allowDecimals={false} />
                  <Tooltip contentStyle={{ background: "#0F172A", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12 }} />
                  <Line type="monotone" dataKey="count" stroke="#10B981" strokeWidth={2.5} dot={{ fill: "#10B981" }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="card p-5">
              <h3 className="font-bold text-white mb-4">Müsaitlik</h3>
              <div className="text-4xl font-extrabold text-[#EF4444]">{stats.availability.busy_records}</div>
              <div className="text-sm text-slate-400">toplam DOLU kaydı</div>
              <div className="text-4xl font-extrabold text-[#10B981] mt-4">{stats.availability.total_resources}</div>
              <div className="text-sm text-slate-400">toplam kaynak</div>
            </div>
          </div>
        </div>
      )}

      {tab === "firmalar" && (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <select data-testid="company-category-filter" className="input !w-auto" value={companyCat} onChange={(e) => setCompanyCat(e.target.value)}>
              <option value="">Tüm Kategoriler</option>
              {categories.filter((c) => !c.parent_id).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <span className="text-xs text-slate-500" data-testid="company-filter-count">{companies.length} firma listeleniyor</span>
          </div>
          <div className="card overflow-x-auto">
          <table className="w-full">
            <thead className="bg-white/5"><tr><th className="th">Firma</th><th className="th">Konum</th><th className="th">Sahip</th><th className="th">Durum</th><th className="th">Doğrulama</th><th className="th">Görünürlük</th><th className="th">Plan</th><th className="th">İşlem</th></tr></thead>
            <tbody className="divide-y divide-white/5">
              {companies.map((c) => (
                <>
                  <tr key={c.id} data-testid={`admin-table-row-${c.id}`}>
                    <td className="td">
                      <button onClick={() => toggleCompanyResources(c)} className="font-semibold text-left hover:text-[#D4AF37]">{c.name}</button>
                      <div className="text-xs text-slate-500">{c.resource_count} kaynak</div>
                    </td>
                    <td className="td">{c.city} / {c.district}</td>
                    <td className="td text-xs">{c.owner_email}</td>
                    <td className="td">
                      <button data-testid={`company-status-${c.id}`} onClick={() => updateCompany(c.id, { status: c.status === "active" ? "passive" : "active" })}
                        className={c.status === "active" ? "badge-green" : "badge-red"}>{c.status === "active" ? "Aktif" : "Pasif"}</button>
                    </td>
                    <td className="td">
                      <button data-testid={`company-verify-${c.id}`} onClick={() => updateCompany(c.id, { verified: !c.verified })}
                        className={c.verified ? "badge-gold" : "badge-red"}>{c.verified ? "Onaylı" : "Onaysız"}</button>
                    </td>
                    <td className="td">
                      <select data-testid={`company-visibility-${c.id}`} className="input !py-1.5 !px-2 !text-xs !w-auto" value={c.visibility} onChange={(e) => updateCompany(c.id, { visibility: e.target.value })}>
                        <option value="ILCE">İlçe</option><option value="IL">İl</option><option value="TURKIYE">Türkiye</option>
                      </select>
                    </td>
                    <td className="td">
                      <select className="input !py-1.5 !px-2 !text-xs !w-auto" value={c.plan} onChange={(e) => updateCompany(c.id, { plan: e.target.value })}>
                        {["FREE", "STANDARD", "PRO", "PREMIUM"].map((p) => <option key={p} value={p}>{p}</option>)}
                      </select>
                    </td>
                    <td className="td">
                      <div className="flex gap-2">
                        <button data-testid={`impersonate-${c.id}`} onClick={() => doImpersonate(c)} className="btn-ghost !py-1.5 !px-3 text-xs"><LogIn size={13} /> Firma olarak gir</button>
                        <button data-testid={`company-delete-${c.id}`} onClick={() => window.confirm("Firma ve tüm kaynakları silinsin mi?") && run(api.delete(`/admin/companies/${c.id}`), "Silindi").then(() => setCompanies(companies.filter((x) => x.id !== c.id)))} className="btn-danger"><Trash2 size={13} /></button>
                      </div>
                    </td>
                  </tr>
                  {expandedCompany === c.id && (
                    <tr key={`${c.id}-res`}>
                      <td colSpan={8} className="td bg-[#070C1E]/60">
                        <div className="flex flex-wrap gap-2 py-2">
                          {companyResources.map((r) => (
                            <span key={r.id} className="badge-gold">{r.name} · {r.busy_dates} dolu gün</span>
                          ))}
                          {!companyResources.length && <span className="text-xs text-slate-500">Kaynak yok</span>}
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {tab === "kullanicilar" && (
        <div className="card overflow-x-auto">
          <table className="w-full">
            <thead className="bg-white/5"><tr><th className="th">Ad</th><th className="th">E-posta</th><th className="th">Rol</th><th className="th">Durum</th><th className="th">İşlem</th></tr></thead>
            <tbody className="divide-y divide-white/5">
              {users.map((u) => (
                <tr key={u.id} data-testid={`user-row-${u.id}`}>
                  <td className="td font-semibold">{u.name} {u.surname}</td>
                  <td className="td">{u.email}</td>
                  <td className="td">
                    <select className="input !py-1.5 !px-2 !text-xs !w-auto" value={u.role}
                      onChange={(e) => run(api.put(`/admin/users/${u.id}`, { role: e.target.value }), "Rol güncellendi").then(() => api.get("/admin/users").then((r) => setUsers(r.data)))}>
                      {["CUSTOMER", "COMPANY", "ADMIN", "SUPER_ADMIN"].map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </td>
                  <td className="td">
                    <button onClick={() => run(api.put(`/admin/users/${u.id}`, { status: u.status === "active" ? "passive" : "active" }), "Güncellendi").then(() => api.get("/admin/users").then((r) => setUsers(r.data)))}
                      className={u.status === "active" ? "badge-green" : "badge-red"}>{u.status === "active" ? "Aktif" : "Pasif"}</button>
                  </td>
                  <td className="td">
                    <div className="flex gap-2">
                      <button data-testid={`user-passreset-${u.id}`} className="btn-ghost !py-1.5 !px-3 text-xs"
                        onClick={async () => {
                          const p = window.prompt(`${u.email} için yeni şifre (min 6 karakter):`);
                          if (p) await run(api.put(`/admin/users/${u.id}/password`, { new_password: p }), "Şifre sıfırlandı");
                        }}>
                        Şifre Sıfırla
                      </button>
                      <button onClick={() => window.confirm("Kullanıcı silinsin mi?") && run(api.delete(`/admin/users/${u.id}`), "Silindi").then(() => setUsers(users.filter((x) => x.id !== u.id)))} className="btn-danger"><Trash2 size={13} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "kategoriler" && (
        <div className="space-y-5">
          <form className="card p-5 flex flex-wrap gap-3 items-end" onSubmit={(e) => { e.preventDefault(); run(api.post("/admin/categories", { ...newCat, parent_id: newCat.parent_id || null, order: Number(newCat.order) }), "Kategori eklendi").then(() => api.get("/admin/categories").then((r) => setCategories(r.data))); setNewCat({ name: "", parent_id: "", order: 99 }); }}>
            <div className="flex-1 min-w-[180px]"><label className="label">Kategori Adı</label><input data-testid="category-name-input" className="input" required value={newCat.name} onChange={(e) => setNewCat({ ...newCat, name: e.target.value })} /></div>
            <div><label className="label">Üst Kategori</label>
              <select data-testid="category-parent-select" className="input !w-auto" value={newCat.parent_id} onChange={(e) => setNewCat({ ...newCat, parent_id: e.target.value })}>
                <option value="">Yok (Ana kategori)</option>
                {parentCats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div><label className="label">Sıra</label><input type="number" className="input !w-24" value={newCat.order} onChange={(e) => setNewCat({ ...newCat, order: e.target.value })} /></div>
            <button data-testid="category-add-btn" className="btn-gold !py-3"><Plus size={16} /> Ekle</button>
          </form>
          <div className="card overflow-x-auto">
            <table className="w-full">
              <thead className="bg-white/5"><tr><th className="th">Ad</th><th className="th">Üst Kategori</th><th className="th">Sıra</th><th className="th">Durum</th><th className="th">İşlem</th></tr></thead>
              <tbody className="divide-y divide-white/5">
                {categories.map((c) => (
                  <tr key={c.id} data-testid={`category-row-${c.id}`}>
                    <td className="td font-semibold">{c.parent_id ? "— " : ""}{c.name}</td>
                    <td className="td">{categories.find((p) => p.id === c.parent_id)?.name || "-"}</td>
                    <td className="td">{c.order}</td>
                    <td className="td">
                      <button data-testid={`category-toggle-${c.id}`} onClick={() => run(api.put(`/admin/categories/${c.id}`, { name: c.name, icon: c.icon, order: c.order, active: !c.active, parent_id: c.parent_id }), "Güncellendi").then(() => api.get("/admin/categories").then((r) => setCategories(r.data)))}
                        className={c.active ? "badge-green" : "badge-red"}>{c.active ? "Aktif" : "Pasif"}</button>
                    </td>
                    <td className="td">
                      <button data-testid={`category-delete-${c.id}`} onClick={() => window.confirm("Kategori silinsin mi?") && run(api.delete(`/admin/categories/${c.id}`), "Silindi").then(() => api.get("/admin/categories").then((r) => setCategories(r.data)))} className="btn-danger"><Trash2 size={13} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "konumlar" && (
        <div className="grid lg:grid-cols-2 gap-5">
          <div className="card p-5">
            <div className="flex gap-2 mb-4">
              <input data-testid="new-city-input" className="input" placeholder="Yeni il adı" value={newCity} onChange={(e) => setNewCity(e.target.value)} />
              <button className="btn-gold !py-2 shrink-0" onClick={() => newCity && run(api.post("/admin/locations/city", { city: newCity }), "İl eklendi").then(() => api.get("/admin/locations").then((r) => setLocations(r.data))) && setNewCity("")}>Ekle</button>
            </div>
            <div className="max-h-[480px] overflow-y-auto space-y-1">
              {locations.map((l) => (
                <button key={l.city} data-testid={`city-item-${l.city}`} onClick={() => setSelCity(l.city)}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${selCity === l.city ? "bg-[#D4AF37]/15 text-[#D4AF37] font-bold" : "text-slate-300 hover:bg-white/5"}`}>
                  {l.city} <span className="text-xs text-slate-500">({l.districts.length})</span>
                </button>
              ))}
            </div>
          </div>
          <div className="card p-5">
            <h3 className="font-bold text-white mb-3">{selCity} İlçeleri</h3>
            <div className="flex gap-2 mb-4">
              <input data-testid="new-district-input" className="input" placeholder="Yeni ilçe adı" value={newDistrict} onChange={(e) => setNewDistrict(e.target.value)} />
              <button data-testid="add-district-btn" className="btn-gold !py-2 shrink-0" onClick={() => newDistrict && run(api.post("/admin/locations/district", { city: selCity, district: newDistrict }), "İlçe eklendi").then(() => api.get("/admin/locations").then((r) => setLocations(r.data))) && setNewDistrict("")}>Ekle</button>
            </div>
            <div className="flex flex-wrap gap-2 max-h-[420px] overflow-y-auto">
              {(selCityObj?.districts || []).map((d) => (
                <span key={d} className="inline-flex items-center gap-1.5 bg-white/5 border border-white/10 rounded-full px-3 py-1 text-xs text-slate-300">
                  {d}
                  <button onClick={() => run(api.delete("/admin/locations/district", { data: { city: selCity, district: d } }), "Silindi").then(() => api.get("/admin/locations").then((r) => setLocations(r.data)))} className="text-slate-500 hover:text-red-400"><X size={12} /></button>
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === "kaynaklar" && (
        <div className="card overflow-x-auto">
          <table className="w-full">
            <thead className="bg-white/5"><tr><th className="th">Kaynak</th><th className="th">Firma</th><th className="th">Kapasite</th><th className="th">Durum</th><th className="th">İşlem</th></tr></thead>
            <tbody className="divide-y divide-white/5">
              {resources.map((r) => (
                <tr key={r.id} data-testid={`admin-resource-row-${r.id}`}>
                  <td className="td font-semibold">{r.name}</td>
                  <td className="td">{r.company_name}</td>
                  <td className="td">{r.max_capacity ? `${r.min_capacity}-${r.max_capacity}` : "-"}</td>
                  <td className="td">
                    <button onClick={() => run(api.put(`/admin/resources/${r.id}`), "Güncellendi").then(() => api.get("/admin/resources").then((x) => setResources(x.data)))}
                      className={r.active ? "badge-green" : "badge-red"}>{r.active ? "Aktif" : "Pasif"}</button>
                  </td>
                  <td className="td"><button onClick={() => window.confirm("Kaynak silinsin mi?") && run(api.delete(`/admin/resources/${r.id}`), "Silindi").then(() => setResources(resources.filter((x) => x.id !== r.id)))} className="btn-danger"><Trash2 size={13} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "musaitlikler" && (
        <div className="card overflow-x-auto">
          <table className="w-full">
            <thead className="bg-white/5"><tr><th className="th">Kaynak</th><th className="th">Tarih</th><th className="th">Durum</th><th className="th">Rezervasyon Detayı</th></tr></thead>
            <tbody className="divide-y divide-white/5">
              {availability.map((a) => (
                <tr key={a.id}><td className="td font-semibold">{a.resource_name}</td><td className="td">{a.date}</td><td className="td"><span className="badge-red">{a.status}</span></td>
                  <td className="td text-xs text-slate-400 max-w-[260px]">
                    {a.details ? [
                      a.details.event_time && `Saat ${a.details.event_time}`,
                      a.details.gelin_alma && `Gelin alma${a.details.gelin_alma_saati ? ` ${a.details.gelin_alma_saati}` : ""}`,
                      a.details.yemekte_cekim && "Yemekte çekim",
                      a.details.notes,
                    ].filter(Boolean).join(" · ") : "-"}
                  </td>
                </tr>
              ))}
              {!availability.length && <tr><td colSpan={4} className="td text-slate-500">Dolu kaydı yok.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === "reklamlar" && (
        <div className="space-y-5">
          <div className="flex justify-end">
            <button data-testid="add-ad-btn" onClick={() => setAdModal({ form: { ...EMPTY_AD } })} className="btn-gold !py-2.5"><Plus size={16} /> Reklam Ekle</button>
          </div>
          <div className="card overflow-x-auto">
            <table className="w-full">
              <thead className="bg-white/5"><tr><th className="th">Reklam</th><th className="th">Alan</th><th className="th">Tarih Aralığı</th><th className="th">Hedefleme</th><th className="th">Gösterim</th><th className="th">Tıklanma</th><th className="th">Durum</th><th className="th">İşlem</th></tr></thead>
              <tbody className="divide-y divide-white/5">
                {ads.map((a) => (
                  <tr key={a.id} data-testid={`ad-row-${a.id}`}>
                    <td className="td font-semibold">{a.name}</td>
                    <td className="td text-xs">{PLACEMENTS.find((p) => p[0] === a.placement)?.[1] || a.placement}</td>
                    <td className="td text-xs">{a.start_date} → {a.end_date}</td>
                    <td className="td text-xs">{[a.city, a.district, categories.find((c) => c.id === a.category_id)?.name].filter(Boolean).join(" / ") || "Tümü"}</td>
                    <td className="td">{a.views}</td>
                    <td className="td">{a.clicks}</td>
                    <td className="td"><span className={a.active ? "badge-green" : "badge-red"}>{a.active ? "Aktif" : "Pasif"}</span></td>
                    <td className="td">
                      <div className="flex gap-2">
                        <button data-testid={`ad-edit-${a.id}`} onClick={() => setAdModal({ id: a.id, form: { ...a } })} className="btn-ghost !py-1.5 !px-3 text-xs">Düzenle</button>
                        <button onClick={() => window.confirm("Reklam silinsin mi?") && run(api.delete(`/admin/ads/${a.id}`), "Silindi").then(() => setAds(ads.filter((x) => x.id !== a.id)))} className="btn-danger"><Trash2 size={13} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!ads.length && <tr><td colSpan={8} className="td text-slate-500">Reklam yok.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "talepler" && (
        <div className="card overflow-x-auto">
          <table className="w-full">
            <thead className="bg-white/5"><tr><th className="th">Ad</th><th className="th">Firma</th><th className="th">Telefon</th><th className="th">Tarih</th><th className="th">Durum</th></tr></thead>
            <tbody className="divide-y divide-white/5">
              {requests.map((r) => (
                <tr key={r.id}><td className="td font-semibold">{r.name}</td><td className="td">{r.company_name}</td><td className="td">{r.phone}</td><td className="td">{r.event_date || "-"}</td><td className="td">{r.status}</td></tr>
              ))}
              {!requests.length && <tr><td colSpan={5} className="td text-slate-500">Talep yok.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === "abonelikler" && plans && (
        <div className="card p-6 max-w-xl space-y-4">
          <p className="text-sm text-slate-400">Plan başına kaynak limitleri (-1 = sınırsız):</p>
          {Object.entries(plans).map(([plan, limit]) => (
            <div key={plan} className="flex items-center gap-4">
              <span className="w-28 font-bold text-white text-sm">{plan}</span>
              <input data-testid={`plan-limit-${plan}`} type="number" className="input !w-32" value={plans[plan]}
                onChange={(e) => setPlans({ ...plans, [plan]: Number(e.target.value) })} />
              <span className="text-xs text-slate-500">kaynak</span>
            </div>
          ))}
          <button data-testid="plans-save-btn" className="btn-gold" onClick={() => run(api.put("/admin/plans", plans), "Planlar güncellendi")}>Kaydet</button>
        </div>
      )}

      {tab === "loglar" && (
        <div className="card overflow-x-auto">
          <table className="w-full">
            <thead className="bg-white/5"><tr><th className="th">Zaman</th><th className="th">Kullanıcı</th><th className="th">İşlem</th><th className="th">Detay</th></tr></thead>
            <tbody className="divide-y divide-white/5">
              {logs.map((l) => (
                <tr key={l.id} data-testid={`log-row-${l.id}`}>
                  <td className="td text-xs">{new Date(l.created_at).toLocaleString("tr-TR")}</td>
                  <td className="td text-xs">{l.actor_email}</td>
                  <td className="td"><span className="badge-gold">{l.action}</span></td>
                  <td className="td text-xs max-w-[300px] truncate">{l.detail}</td>
                </tr>
              ))}
              {!logs.length && <tr><td colSpan={4} className="td text-slate-500">Log kaydı yok.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {adModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="ad-modal" onClick={() => setAdModal(null)}>
          <div className="card w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold text-white">{adModal.id ? "Reklamı Düzenle" : "Reklam Ekle"}</h3>
              <button onClick={() => setAdModal(null)} className="text-slate-400 hover:text-white"><X size={20} /></button>
            </div>
            <form onSubmit={saveAd} className="space-y-4">
              <div><label className="label">Reklam Adı</label><input data-testid="ad-form-name" className="input" required value={adModal.form.name} onChange={(e) => setAdModal({ ...adModal, form: { ...adModal.form, name: e.target.value } })} /></div>
              <div><label className="label">Görsel URL</label><input data-testid="ad-form-image" className="input" required value={adModal.form.image} onChange={(e) => setAdModal({ ...adModal, form: { ...adModal.form, image: e.target.value } })} /></div>
              <div><label className="label">Hedef URL</label><input className="input" value={adModal.form.url} onChange={(e) => setAdModal({ ...adModal, form: { ...adModal.form, url: e.target.value } })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="label">Başlangıç</label><input type="date" className="input" value={adModal.form.start_date} onChange={(e) => setAdModal({ ...adModal, form: { ...adModal.form, start_date: e.target.value } })} /></div>
                <div><label className="label">Bitiş</label><input type="date" className="input" value={adModal.form.end_date} onChange={(e) => setAdModal({ ...adModal, form: { ...adModal.form, end_date: e.target.value } })} /></div>
                <div><label className="label">Öncelik</label><input type="number" className="input" value={adModal.form.priority} onChange={(e) => setAdModal({ ...adModal, form: { ...adModal.form, priority: Number(e.target.value) } })} /></div>
                <div>
                  <label className="label">Gösterim Alanı</label>
                  <select data-testid="ad-form-placement" className="input" value={adModal.form.placement} onChange={(e) => setAdModal({ ...adModal, form: { ...adModal.form, placement: e.target.value } })}>
                    {PLACEMENTS.map((p) => <option key={p[0]} value={p[0]}>{p[1]}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label">İl (hedefleme)</label>
                  <select className="input" value={adModal.form.city} onChange={(e) => setAdModal({ ...adModal, form: { ...adModal.form, city: e.target.value } })}>
                    <option value="">Tümü</option>
                    {locations.map((l) => <option key={l.city} value={l.city}>{l.city}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label">Kategori (hedefleme)</label>
                  <select className="input" value={adModal.form.category_id} onChange={(e) => setAdModal({ ...adModal, form: { ...adModal.form, category_id: e.target.value } })}>
                    <option value="">Tümü</option>
                    {parentCats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-300">
                <input type="checkbox" className="accent-[#D4AF37] w-4 h-4" checked={adModal.form.active} onChange={(e) => setAdModal({ ...adModal, form: { ...adModal.form, active: e.target.checked } })} />
                Aktif
              </label>
              <button data-testid="ad-form-save" className="btn-gold w-full">Kaydet</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
